import { WatchlistItem } from './store';
import { tmdb, Show } from './tmdb';

export interface UpdateSchedule {
  showId: number;
  nextCheckDate: string;
  updateFrequency: number; // hours
  reason: string;
}

export class UpdateScheduler {
  /**
   * Calculate the optimal update frequency and next check date for a show
   * based on its status and next episode air date
   */
  static calculateUpdateSchedule(show: Show): UpdateSchedule {
    const now = new Date();
    const status = show.status || 'Returning Series';
    const nextEpisode = show.next_episode_to_air;

    // Default schedule
    let updateFrequency = 24; // hours
    let reason = 'Default schedule';

    // Ended or Cancelled shows - very low priority
    if (status === 'Ended' || status === 'Cancelled') {
      updateFrequency = 4320; // 6 months in hours
      reason = `Show is ${status.toLowerCase()} - checking every 6 months`;
    }
    // Pilot status - check monthly
    else if (status === 'Pilot') {
      updateFrequency = 720; // 30 days in hours
      reason = 'Pilot episode - checking monthly for series pickup';
    }
    // In Production - check bi-weekly
    else if (status === 'In Production') {
      updateFrequency = 336; // 2 weeks in hours
      reason = 'In production - checking bi-weekly for premiere date';
    }
    // Returning Series or Planned - check based on next episode timing
    else if (status === 'Returning Series' || status === 'Planned') {
      if (nextEpisode && nextEpisode.air_date) {
        const airDate = new Date(nextEpisode.air_date);
        const daysUntilAir = (airDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

        if (daysUntilAir < 0) {
          // Episode should have aired already - check frequently for next episode
          updateFrequency = 6; // 6 hours (4x daily)
          reason = 'New episode just aired - checking 4x daily for next episode';
        } else if (daysUntilAir <= 7) {
          // Episode airs within a week - high priority
          updateFrequency = 6; // 6 hours (4x daily)
          reason = `Next episode in ${Math.ceil(daysUntilAir)} days - checking 4x daily`;
        } else if (daysUntilAir <= 30) {
          // Episode airs within a month - medium priority
          updateFrequency = 24; // 24 hours (daily)
          reason = `Next episode in ${Math.ceil(daysUntilAir)} days - checking daily`;
        } else if (daysUntilAir <= 90) {
          // Episode airs within 3 months - low priority
          updateFrequency = 168; // 1 week
          reason = `Next episode in ${Math.ceil(daysUntilAir)} days - checking weekly`;
        } else {
          // Episode airs 3+ months away - very low priority
          updateFrequency = 336; // 2 weeks
          reason = `Next episode in ${Math.ceil(daysUntilAir)} days - checking bi-weekly`;
        }
      } else {
        // No next episode scheduled - check weekly
        updateFrequency = 168; // 1 week
        reason = 'No next episode scheduled - checking weekly';
      }
    }

    // Calculate next check date
    const nextCheckDate = new Date(now.getTime() + (updateFrequency * 60 * 60 * 1000));

    return {
      showId: show.id,
      nextCheckDate: nextCheckDate.toISOString(),
      updateFrequency,
      reason,
    };
  }

  /**
   * Check if a show is due for an update
   */
  static isDueForUpdate(item: WatchlistItem): boolean {
    if (!item.nextCheckDate) {
      // No check date set - needs initial check
      return true;
    }

    const now = new Date();
    const nextCheck = new Date(item.nextCheckDate);

    return now >= nextCheck;
  }

  /**
   * Get all shows that are due for update
   */
  static getShowsDueForUpdate(watchlist: WatchlistItem[]): WatchlistItem[] {
    const tvShows = watchlist.filter(item => item.type === 'tv');
    return tvShows.filter(show => this.isDueForUpdate(show));
  }

  /**
   * Fetch full show details and calculate update schedule
   */
  static async fetchShowDetailsWithSchedule(showId: number): Promise<{
    show: Show;
    schedule: UpdateSchedule;
  }> {
    const show = await tmdb.getShow(showId);
    const schedule = this.calculateUpdateSchedule(show);

    return { show, schedule };
  }

  /**
   * Parse TMDB status string to our normalized format
   */
  static normalizeStatus(tmdbStatus?: string): WatchlistItem['status'] {
    if (!tmdbStatus) return undefined;

    const statusLower = tmdbStatus.toLowerCase();

    if (statusLower.includes('returning')) return 'Returning Series';
    if (statusLower.includes('ended')) return 'Ended';
    if (statusLower.includes('cancel')) return 'Cancelled';
    if (statusLower.includes('production')) return 'In Production';
    if (statusLower.includes('pilot')) return 'Pilot';
    if (statusLower.includes('plan')) return 'Planned';

    return undefined;
  }
}
